import { Box, Flex, Text, useToast } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ActionListButton, ActionPanelContent } from "./NavItems/ActionList";
import { RoundListButton, RoundPanelContent } from "./NavItems/RoundList";
import { AIAssistButton, ReopenDefaultButton } from "../../../components/Layout/Navbar/Navbar";
import { useRightSidebar } from "../../../context/RightSidebarContext";
import { WarningCircle } from "@phosphor-icons/react";
import questionnaireService from "../../../api/issb/questionnaire.service";
import { useRefreshRoundList, useSelectRound } from "../QuestionnaireRound/RoundService";
import { showErrorToast, showSuccessToast } from "../../../utils";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";

/**
 * Secondary header with kebab actions, round selector, AI assist, and optional left panel.
 * Wraps page content so the panel column sits alongside it.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Page content
 * @param {function} [props.onPanelChange] - Called with panel name or null when panel opens/closes
 */
const HEADER_HEIGHT = 52;

const SecondaryHeader = ({ children, onPanelChange, showRoundSelector = true, showActions = true }) => {
  const [openPanel, setOpenPanel] = useState(null);
  const { setTopOffset, reopenableLabel } = useRightSidebar();
  const panelRef = useRef(null);
  const headerRef = useRef(null);
  const roundSelector = useSelector((state) => state.issbRound);
  const isDraft = roundSelector?.selectedRound?.state === "DRAFT";
  const dispatch = useDispatch();
  const toast = useToast();
  const { refreshRoundList } = useRefreshRoundList();
  const selectRound = useSelectRound();

  const handleStartRound = () => {
    const selectedRound = roundSelector?.selectedRound;
    if (!selectedRound) return;
    dispatch(showAppSpinner());
    questionnaireService
      .create({ ...selectedRound, state: "ACTIVE" })
      .then((updated) => {
        refreshRoundList().then(() => {
          selectRound(updated);
          showSuccessToast(toast, "Round started successfully");
        });
      })
      .catch((err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message))
      .finally(() => dispatch(hideAppSpinner()));
  };

  useEffect(() => {
    setTopOffset(HEADER_HEIGHT);
    return () => setTopOffset(0);
  }, [setTopOffset]);

  // Close panel when clicking outside the panel and header
  useEffect(() => {
    if (!openPanel) return;
    const handleClickOutside = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        headerRef.current && !headerRef.current.contains(e.target)
      ) {
        setOpenPanel(null);
        onPanelChange?.(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openPanel, onPanelChange]);

  const togglePanel = (panel) => {
    const next = openPanel === panel ? null : panel;
    setOpenPanel(next);
    onPanelChange?.(next);
  };

  const closePanel = () => {
    setOpenPanel(null);
    onPanelChange?.(null);
  };

  return (
    <Flex direction="column" width="100%" h="100%">
      <Flex
        ref={headerRef}
        position="sticky"
        top={0}
        zIndex={10}
        bg="white"
        h="52px"
        flexShrink={0}
        borderBottom="1px solid"
        borderColor="gray.200"
      >
        {showActions && (
          <Box borderRight="1px solid" borderColor="gray.200" display="flex" alignItems="center" justifyContent="center" w="52px" bg="white">
            <ActionListButton isOpen={openPanel === "actions"} onToggle={() => togglePanel("actions")} />
          </Box>
        )}
        {showRoundSelector && (
          <Box px={5} borderRight="1px solid" borderColor="gray.200" display="flex" alignItems="center" bg="white">
            <RoundListButton isOpen={openPanel === "rounds"} onToggle={() => togglePanel("rounds")} />
          </Box>
        )}
        <Box flex={1} display="flex" alignItems="center" justifyContent="flex-end">
          {isDraft && (
            <Flex align="center" gap={2} px={4} flex={1}>
              <WarningCircle size={16} color="var(--chakra-colors-orange-500)" weight="fill" />
              <Text fontSize="xs" color="gray.600">
                This round is not yet started.{" "}
                <Text as="span" fontSize="xs" color="limeShade.600" cursor="pointer" textDecoration="underline" _hover={{ color: "limeShade.700" }} onClick={handleStartRound}>
                  Start round
                </Text>{" "}
                to make it available to your team.
              </Text>
            </Flex>
          )}
        </Box>
        <Box display="flex" alignItems="center" justifyContent="flex-end">
          {/* Only renders once a page's closable default tray (Manage) has been closed. */}
          {reopenableLabel ? (
            <Box borderLeft="1px solid" borderColor="gray.200" h="100%" display="flex" alignItems="center" px={4} zIndex={11} bg="white">
              <ReopenDefaultButton />
            </Box>
          ) : null}
          <Box borderLeft="1px solid" borderColor="gray.200" h="100%" display="flex" alignItems="center" px={4} zIndex={11} bg="white">
            <AIAssistButton />
          </Box>
        </Box>
      </Flex>

      <Flex flex={1} minH={0}>
        {openPanel && (
          <Box
            ref={panelRef}
            w="300px"
            flexShrink={0}
            borderRight="1px solid"
            borderColor="gray.200"
            bg="white"
            position="sticky"
            top="52px"
            alignSelf={openPanel === "actions" ? "stretch" : "flex-start"}
            minH={openPanel === "actions" ? "calc(100vh - 52px - 71px)" : undefined}
            maxH="calc(100vh - 52px - 71px)"
            overflowY="auto"
            sx={{
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
              "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
              scrollbarWidth: "thin",
              scrollbarColor: "transparent transparent",
              "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
            }}
          >
            {openPanel === "actions" && <ActionPanelContent onClose={closePanel} />}
            {openPanel === "rounds" && <RoundPanelContent onClose={closePanel} />}
          </Box>
        )}

        <Box flex={1} overflowY="auto">
          {children}
        </Box>
      </Flex>
    </Flex>
  );
};

export default SecondaryHeader;
